// pages/CompleteProfile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  Cake as CakeIcon,
  Wc as WcIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Coffee as CoffeeIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const CompleteProfile = ({keywordsData, language}) => {
  const [formData, setFormData] = useState({
    age: '',
    sex: '',
    user_phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, updateUser, needsProfileCompletion } = useAuth();
  const navigate = useNavigate();

  const completePrfoileTxt = language === 'ar' ? 'إكمال الحساب' : 'Complete profile';
  const completeingPrfoileTxt = language === 'ar' ? 'جار إكمال الحساب...' : 'Processing ....';

  useEffect(() => {
    if (!needsProfileCompletion) {
      navigate('/');
    }
  }, [needsProfileCompletion, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.put('/auth/complete-profile', formData);
      updateUser(response.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || language === 'ar' ? 'فشل تحديث الملف الشخصي' : 'field to Update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ 
      py: 6, 
      mb: 10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Paper elevation={0} sx={{ 
        p: { xs: 3, sm: 5 },
        width: '100%',
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          border: '1px solid #FFD700',
          boxShadow: '0 12px 48px rgba(255, 215, 0, 0.25)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #FFD700, #FFE55C, #FFD700)',
        }
      }}>
        {/* Decorative Coffee Icon */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          opacity: 0.1,
          transform: 'rotate(15deg)',
          display: { xs: 'none', sm: 'block' }
        }}>
          <CoffeeIcon sx={{ fontSize: 80, color: '#FFD700' }} />
        </Box>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            border: '2px solid #FFD700'
          }}>
            <CoffeeIcon sx={{ fontSize: 48, color: '#FFD700' }} />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ 
            color: '#FFD700',
            fontWeight: 900,
            letterSpacing: '2px',
            mb: 1
          }}>
            {language === 'ar' ? 'مرحباً بك في' : 'Welcome in'}
          </Typography>
          <Typography variant="h3" sx={{ 
            color: '#FFD700',
            fontWeight: 900,
            letterSpacing: '3px',
            mb: 1,
            fontSize: { xs: '2rem', sm: '2.5rem' }
          }}>
            RETO
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#FFD700',
            fontWeight: 600,
            fontStyle: 'italic',
            mb: 2
          }}>
            cafe
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ 
          color: '#B3B3B3', 
          mb: 4, 
          textAlign: 'center',
          fontSize: '1.1rem',
          lineHeight: 1.6
        }}>
          {language === 'ar' ? 'يرجى تقديم بعض المعلومات الإضافية لاستكمال حسابك والاستمتاع بتجربة استثنائية' : 'Please provide some additional information to complete your account and enjoy an exceptional experience.'}
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4,
              backgroundColor: 'rgba(255, 61, 0, 0.1)',
              color: '#FF3D00',
              border: '1px solid #FF3D00',
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                color: '#FF3D00',
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Age Field */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CakeIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.95rem', fontWeight: 600 }}>
                {language === 'ar' ? 'العمر' : 'Age'}
              </Typography>
            </Box>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'أدخل عمرك' : 'Enter your age'}
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              required
              inputProps={{ min: 1, max: 120 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  borderRadius: '14px',
                  '& fieldset': {
                    borderColor: '#2D2D2D',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#FFD700',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#FFD700',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#6B6B6B',
                  opacity: 1,
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              }}
            />
          </Box>

          {/* Gender Field */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <WcIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.95rem', fontWeight: 600 }}>
                {language === 'ar' ? 'الجنس' : 'Sex'}
              </Typography>
            </Box>
            <FormControl fullWidth required>
              <Select
                name="sex"
                value={formData.sex}
                onChange={handleChange}
                displayEmpty
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: formData.sex ? '#FFFFFF' : '#6B6B6B',
                  borderRadius: '14px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2D2D2D',
                    borderWidth: '1.5px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FFD700',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FFD700',
                    borderWidth: '2px',
                  },
                  '& .MuiSelect-icon': {
                    color: '#B3B3B3',
                  },
                  '& .MuiSelect-icon:hover': {
                    color: '#FFD700',
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ color: '#6B6B6B' }}>
                  {language === 'ar' ? 'اختر الجنس' : 'Choose sex type'}
                </MenuItem>
                <MenuItem value="male" sx={{ 
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' },
                  '&.Mui-selected': { 
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    '&:hover': { backgroundColor: '#FFE55C' }
                  }
                }}>
                  {language === 'ar' ? 'ذكر' : 'Male'}
                </MenuItem>
                <MenuItem value="female" sx={{ 
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' },
                  '&.Mui-selected': { 
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    '&:hover': { backgroundColor: '#FFE55C' }
                  }
                }}>
                  {language === 'ar' ? 'انثى' : 'Female'}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Phone Field */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.95rem', fontWeight: 600 }}>
                {language === 'ar' ? 'رقم الهاتف' : 'Phone number'}
              </Typography>
            </Box>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'مثال: +٦٠ ١٢١٢٩٩٢٨٣٢٤' : 'Examble: +60 12129928324'}
              name="user_phone"
              value={formData.user_phone}
              onChange={handleChange}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  borderRadius: '14px',
                  '& fieldset': {
                    borderColor: '#2D2D2D',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#FFD700',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#FFD700',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#6B6B6B',
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Welcome Points Message */}
          <Box sx={{ 
            mb: 4,
            p: 2.5,
            backgroundColor: 'rgba(255, 215, 0, 0.05)',
            borderRadius: '16px',
            border: '1px solid #2D2D2D',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 700, mb: 0.5 }}>
              {language === 'ar' ? '🎁 هدية ترحيبية' : '🎁 Welcome gift'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'أكمل بياناتك واحصل على ٥٠ نقطة ترحيبية! ' : 'Complete your details and get 50 welcome points!'}
            </Typography>
          </Box>

          <Divider sx={{ 
            backgroundColor: '#2D2D2D',
            height: '1px',
            mb: 3
          }} />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            size="large"
            startIcon={<SaveIcon />}
            sx={{ 
              py: 1.8,
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              fontSize: '1.1rem',
              borderRadius: '50px',
              border: '1px solid #FFD700',
              '&:hover': {
                backgroundColor: '#FFE55C',
                boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                transform: 'translateY(-2px)',
              },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B',
                border: '1px solid #2D2D2D',
              },
              transition: 'all 0.2s ease'
            }}
          >
            {loading ? completeingPrfoileTxt : completePrfoileTxt}
          </Button>

          {/* Footer Note */}
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              textAlign: 'center',
              color: '#6B6B6B',
              mt: 3,
              fontSize: '0.8rem'
            }}
          >
            {language === 'ar' ? '* سيتم استخدام هذه المعلومات لتحسين تجربتك في RETO Cafe' : 'This information will be used to improve your experience at RETO Cafe'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompleteProfile;